import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { SearchBar } from '@fiverr-private/search_bar';
import { translate } from '@fiverr-private/i18n-react';
import { usePlaceholderFade } from '../../shared/placeholderFade';
import { useTypewriterPlaceholder } from '../../shared/typewriter';
import { SEARCH_INPUT_SELECTOR } from './constants';

type PlaceholderAnimation = 'fade' | 'typewriter';

type PlaceholderAnimationHook = (
    containerRef: React.RefObject<HTMLElement>,
    phrases: string[],
    options: { staticText: string; selector: string }
) => boolean;

// Fixed per instance, so the chosen hook is stable across renders.
const PLACEHOLDER_ANIMATION_HOOKS: Record<PlaceholderAnimation, PlaceholderAnimationHook> = {
    fade: usePlaceholderFade,
    typewriter: useTypewriterPlaceholder,
};

interface Props {
    placeholderKey: string;
    phraseKeys: string[];
    placeholderAnimation?: PlaceholderAnimation;
    source: string;
    theme?: string;
    submitButtonType?: string;
    hideClearButton?: boolean;
    onFocus?: () => void;
    onSearch?: (query: string) => void;
    searchWillSubmit?: () => boolean;
}

export interface AnimatedSearchBarHandle {
    setValue: (text: string) => void;
}

const AnimatedSearchBar = forwardRef<AnimatedSearchBarHandle, Props>(
    ({ placeholderKey, phraseKeys, placeholderAnimation = 'fade', onFocus, ...searchBarProps }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const searchBarRef = useRef<React.ElementRef<typeof SearchBar>>(null);
        const placeholder = translate(placeholderKey) as string;
        const phrases = useMemo(() => phraseKeys.map((key) => translate(key) as string), [phraseKeys]);
        const usePlaceholderAnimation = PLACEHOLDER_ANIMATION_HOOKS[placeholderAnimation];

        usePlaceholderAnimation(containerRef, phrases, {
            staticText: placeholder,
            selector: SEARCH_INPUT_SELECTOR,
        });

        useImperativeHandle(ref, () => ({
            setValue: (text: string) => searchBarRef.current?.setSearch(text),
        }));

        return (
            <Container ref={containerRef} width="100%" maxWidth="100%" onFocus={onFocus}>
                <SearchBar ref={searchBarRef} placeholder={placeholder} {...searchBarProps} />
            </Container>
        );
    }
);

AnimatedSearchBar.displayName = 'AnimatedSearchBar';

export default AnimatedSearchBar;
